// Shared builders for schema.org JSON-LD objects. Each function returns a
// plain object ready to be passed to <StructuredData data={...} />. Keeping
// these as small pure functions (rather than JSX/microdata spread across
// components) means a page's JSON-LD can be built directly from the same
// Contentful data already powering its visible content.

export interface IBreadcrumbItem {
  text: string;
  link: string;
}

// Joins URL segments with single slashes, without mangling the
// "https://" in the first (origin) segment.
export const joinUrl = (...parts: string[]) =>
  parts
    .filter(Boolean)
    .map((part, index) =>
      index === 0 ? part.replace(/\/+$/, "") : part.replace(/^\/+|\/+$/g, "")
    )
    .join("/");

// Generic WebPage schema for page types whose content is too open-ended
// to exhaustively model (e.g. Page.tsx's page-builder of ~27 different
// content-block types) or that otherwise have no more specific schema.org
// type of their own. Partial JSON-LD coverage is normal and expected -
// per Google's own structured data guidelines, it's better to supply a
// few accurate properties than to force-fit a more detailed type without
// good data for it. This just ensures the page has *some* machine-
// readable identity instead of none.
export const buildWebPageSchema = ({
  name,
  description,
  url,
}: {
  name: string;
  description?: string;
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name,
  url,
  ...(description && { description }),
});

export const buildOrganizationSchema = ({
  name,
  url,
  logoUrl,
  description,
  sameAs,
}: {
  name: string;
  url: string;
  logoUrl?: string;
  description?: string;
  sameAs?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${url}#organization`,
  name,
  url,
  ...(logoUrl && { logo: logoUrl }),
  ...(description && { description }),
  ...(sameAs && sameAs.length > 0 && { sameAs }),
});

export const buildWebsiteSchema = ({
  name,
  url,
}: {
  name: string;
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${url}#website`,
  name,
  url,
  // Inlined (not a bare "@id" reference) so this object is self-describing
  // even for consumers that don't merge separate JSON-LD <script> tags -
  // see the note on buildArticleSchema's `organization` parameter.
  publisher: { "@id": `${url}#organization`, "@type": "Organization", name },
});

// Builds an Article schema. `organization` both inlines the publisher's
// @type/name/url directly on the Article (matching every example in
// Google's own Article structured-data docs, which never rely on a bare
// "@id" reference to a node defined in a separate <script> tag) and
// reuses the same "@id" as the sitewide Organization node (see
// buildOrganizationSchema), so parsers that do merge across blocks still
// resolve it as the same entity. `authorName`, when provided (e.g. a
// byline), is rendered as a Person instead; when absent, Google's own
// guidance is to fall back to the publishing Organization as author.
export const buildArticleSchema = ({
  headline,
  description,
  url,
  imageUrl,
  datePublished,
  dateModified,
  authorName,
  organization,
  articleBody,
}: {
  headline: string;
  description?: string;
  url: string;
  imageUrl?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  organization: { id: string; name: string; url: string };
  articleBody?: string;
}) => {
  const organizationRef = {
    "@id": organization.id,
    "@type": "Organization",
    name: organization.name,
    url: organization.url,
  };

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    ...(description && { description }),
    ...(imageUrl && { image: [imageUrl] }),
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    author: authorName
      ? { "@type": "Person", name: authorName }
      : organizationRef,
    publisher: organizationRef,
    ...(articleBody && { articleBody }),
  };
};

// Builds a PostalAddress. Returns undefined (not an empty object) when no
// usable address fields are present, so callers can safely spread the
// result without needing to add their own presence checks.
export const buildPostalAddress = ({
  streetAddress,
  addressLocality,
  addressCountry,
}: {
  streetAddress?: string;
  addressLocality?: string;
  addressCountry?: string;
}) => {
  if (!streetAddress && !addressLocality && !addressCountry) {
    return undefined;
  }
  return {
    "@type": "PostalAddress",
    ...(streetAddress && { streetAddress }),
    ...(addressLocality && { addressLocality }),
    ...(addressCountry && { addressCountry }),
  };
};

// Builds GeoCoordinates. Returns undefined when latitude/longitude aren't
// both present, for the same reason as buildPostalAddress above.
export const buildGeoCoordinates = (latitude?: number, longitude?: number) => {
  if (latitude === undefined || longitude === undefined) {
    return undefined;
  }
  return { "@type": "GeoCoordinates", latitude, longitude };
};

// Builds a Place-derived schema (LocalBusiness, LodgingBusiness,
// TouristAttraction, LandmarksOrHistoricalBuildings, etc. all share this
// shape). Per Google's LocalBusiness structured-data docs, `name` and
// `address` are the only required properties; `geo`/`telephone`/`image`
// are recommended when available.
export const buildPlaceSchema = ({
  type,
  name,
  description,
  url,
  imageUrl,
  address,
  geo,
  telephone,
}: {
  type: string;
  name: string;
  description?: string;
  url: string;
  imageUrl?: string;
  address?: ReturnType<typeof buildPostalAddress>;
  geo?: ReturnType<typeof buildGeoCoordinates>;
  telephone?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": type,
  name,
  url,
  ...(description && { description }),
  ...(imageUrl && { image: [imageUrl] }),
  ...(address && { address }),
  ...(geo && { geo }),
  ...(telephone && { telephone }),
});

// Builds an Event schema. Per Google's Event structured-data docs, the
// only required properties are `name`, `startDate`, and `location` (with
// `location.name` + `location.address`); `description`/`image`/`endDate`
// are recommended when available. `eventStatus`/`eventAttendanceMode` are
// always the same fixed values here since the site has no way to mark an
// event as cancelled/postponed/online, and every event is an in-person,
// physical-venue listing.
export const buildEventSchema = ({
  name,
  description,
  url,
  imageUrl,
  startDate,
  endDate,
  locationName,
  address,
  geo,
}: {
  name: string;
  description?: string;
  url: string;
  imageUrl?: string;
  startDate: string;
  endDate?: string;
  locationName: string;
  address?: ReturnType<typeof buildPostalAddress>;
  geo?: ReturnType<typeof buildGeoCoordinates>;
}) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  name,
  url,
  startDate,
  ...(endDate && { endDate }),
  ...(description && { description }),
  ...(imageUrl && { image: [imageUrl] }),
  location: {
    "@type": "Place",
    name: locationName,
    ...(address && { address }),
    ...(geo && { geo }),
  },
  eventStatus: "https://schema.org/EventScheduled",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
});

export interface ITouristTripStopInput {
  name: string;
  description?: string;
  geo?: ReturnType<typeof buildGeoCoordinates>;
}

// Builds a TouristTrip - schema.org's dedicated type for "a created
// itinerary of visits to one or more places of interest". Not one of
// Google's documented rich-result features (there's no dedicated SERP
// appearance for it), but valid, useful schema.org vocabulary for
// generic crawlers/LLM agents. The `itinerary`/ItemList/ListItem/
// TouristAttraction nesting matches schema.org's own canonical example
// for this type - day grouping isn't a formal property on Trip, so each
// stop is a flat, sequentially-numbered ListItem across the whole trip,
// with the day folded into the stop's own `name` (e.g. "Day 1: ...")
// where the caller wants that distinction preserved.
export const buildTouristTripSchema = ({
  name,
  description,
  url,
  imageUrl,
  stops,
}: {
  name: string;
  description?: string;
  url: string;
  imageUrl?: string;
  stops: ITouristTripStopInput[];
}) => {
  const validStops = (stops || []).filter((stop) => stop && stop.name);
  if (validStops.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name,
    url,
    ...(description && { description }),
    ...(imageUrl && { image: [imageUrl] }),
    itinerary: {
      "@type": "ItemList",
      itemListElement: validStops.map((stop, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "TouristAttraction",
          name: stop.name,
          ...(stop.description && { description: stop.description }),
          ...(stop.geo && { geo: stop.geo }),
        },
      })),
    },
  };
};

export interface IListItemInput {
  name: string;
  url: string;
}

// Builds a CollectionPage with a nested ItemList - for pages that list
// links to other pages (e.g. a question category index) without
// displaying any answer/detail content of their own. Deliberately not
// FAQPage: Google's guidelines require that marked-up content actually be
// visible on the page, and a link-only listing page has no answer text
// to mark up.
export const buildCollectionPageSchema = (
  name: string,
  items: IListItemInput[] | undefined
) => {
  const validItems = (items || []).filter(
    (item) => item && item.name && item.url
  );
  if (validItems.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: validItems.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        url: item.url,
      })),
    },
  };
};

export interface IFaqQuestionInput {
  name: string;
  answerText: string;
}

// Builds a FAQPage with one or more Question/acceptedAnswer entries. Used
// both for pages dedicated to a single question (a FAQPage with exactly
// one mainEntity is valid per Google's own examples) and for the
// multi-question FAQ accordion embedded in articles. `answerText` is
// plain text (tags stripped, entities decoded) rather than HTML - Google's
// own FAQPage examples show both forms as valid, and plain text keeps
// this consistent with the rest of the site's LLM-oriented JSON-LD
// (Article's articleBody, Itinerary's stop descriptions).
export const buildFaqPageSchema = (
  questions: IFaqQuestionInput[] | undefined
) => {
  const validQuestions = (questions || []).filter(
    (q) => q && q.name && q.answerText
  );
  if (validQuestions.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: validQuestions.map((q) => ({
      "@type": "Question",
      name: q.name,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answerText,
      },
    })),
  };
};

// Builds a BreadcrumbList from the same `items` shape BreadCrumbs.tsx
// already receives ({ text, link }, link relative and without locale
// prefix, matching LocaleNavigationLink's convention). The current page
// (last item) is given a `name` but no `item` URL, per Google's guidance
// that the last entry doesn't need a url.
export const buildBreadcrumbSchema = (
  items: IBreadcrumbItem[] | undefined,
  locale: string
) => {
  const validItems = (items || []).filter((crumb) => crumb && crumb.text);
  if (validItems.length === 0) {
    return null;
  }

  const origin = window.location.origin;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: validItems.map((crumb, index) => {
      const isLastItem = index === validItems.length - 1;
      const listItem: Record<string, any> = {
        "@type": "ListItem",
        position: index + 1,
        name: crumb.text,
      };
      if (!isLastItem && crumb.link) {
        listItem.item = joinUrl(origin, locale, crumb.link);
      }
      return listItem;
    }),
  };
};
